/**
 * CustomScrollbar: Adds an always-visible custom scrollbar to an element
 * Works regardless of system scrollbar settings
 */

export default class CustomScrollbar {
    /**
     * @param {HTMLElement} element - The scrollable element
     * @param {Object} options - Configuration options
     */
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            thumbColor: 'rgba(255, 255, 255, 0.4)',
            thumbHoverColor: 'rgba(255, 255, 255, 0.6)',
            trackColor: 'rgba(255, 255, 255, 0.1)',
            width: 6,
            paddingY: 2,
            paddingRight: 2,
            ...options,
        };

        this.isDragging = false;
        this.startY = 0;
        this.startScrollTop = 0;

        this.init();
    }

    init() {
        // Hide native scrollbar
        this.element.style.scrollbarWidth = 'none';
        this.element.style.msOverflowStyle = 'none';

        // Create scrollbar elements
        this.track = document.createElement('div');
        this.track.className = 'custom-scrollbar-track';

        this.thumb = document.createElement('div');
        this.thumb.className = 'custom-scrollbar-thumb';

        this.track.appendChild(this.thumb);

        // Position track relative to element
        const parent = this.element.parentElement;
        if (getComputedStyle(parent).position === 'static') {
            parent.style.position = 'relative';
        }

        // Insert after the element
        this.element.insertAdjacentElement('afterend', this.track);

        // Apply styles
        this.applyStyles();

        // Bind events
        this.bindEvents();

        // Initial update
        this.update();
    }

    applyStyles() {
        const { thumbColor, trackColor, width, paddingY, paddingRight } = this.options;

        Object.assign(this.track.style, {
            position: 'absolute',
            top: `${paddingY}px`,
            right: `${paddingRight}px`,
            bottom: `${paddingY}px`,
            width: `${width}px`,
            background: trackColor,
            borderRadius: `${width / 2}px`,
            zIndex: '10',
            opacity: '0',
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
        });

        Object.assign(this.thumb.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            background: thumbColor,
            borderRadius: `${width / 2}px`,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            pointerEvents: 'auto',
        });
    }

    bindEvents() {
        // Update on scroll
        this.element.addEventListener('scroll', () => this.updateThumbPosition(), { passive: true });

        // Update on resize
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.element);

        // Also observe content changes via mutation
        this.mutationObserver = new MutationObserver(() => this.update());
        this.mutationObserver.observe(this.element, { childList: true, subtree: true });

        // Thumb drag
        this.thumb.addEventListener('mousedown', (e) => this.onDragStart(e));
        document.addEventListener('mousemove', (e) => this.onDragMove(e));
        document.addEventListener('mouseup', () => this.onDragEnd());

        // Touch support
        this.thumb.addEventListener('touchstart', (e) => this.onDragStart(e), { passive: false });
        document.addEventListener('touchmove', (e) => this.onDragMove(e), { passive: false });
        document.addEventListener('touchend', () => this.onDragEnd());

        // Track click to jump
        this.track.addEventListener('click', (e) => this.onTrackClick(e));

        // Hover effects
        this.thumb.addEventListener('mouseenter', () => {
            this.thumb.style.backgroundColor = this.options.thumbHoverColor;
        });
        this.thumb.addEventListener('mouseleave', () => {
            if (!this.isDragging) {
                this.thumb.style.backgroundColor = this.options.thumbColor;
            }
        });
    }

    update() {
        const { scrollHeight, clientHeight } = this.element;
        const hasOverflow = scrollHeight > clientHeight;

        // Show/hide based on overflow
        this.track.style.opacity = hasOverflow ? '1' : '0';
        this.track.style.pointerEvents = hasOverflow ? 'auto' : 'none';

        if (hasOverflow) {
            // Calculate thumb height
            const trackHeight = this.track.clientHeight;
            const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
            this.thumb.style.height = `${thumbHeight}px`;
            this.updateThumbPosition();
        }
    }

    updateThumbPosition() {
        const { scrollTop, scrollHeight, clientHeight } = this.element;
        const trackHeight = this.track.clientHeight;
        const thumbHeight = this.thumb.clientHeight;
        const maxScroll = scrollHeight - clientHeight;
        const maxThumbTop = trackHeight - thumbHeight;

        if (maxScroll > 0) {
            const thumbTop = (scrollTop / maxScroll) * maxThumbTop;
            this.thumb.style.top = `${thumbTop}px`;
        }
    }

    onDragStart(e) {
        e.preventDefault();
        this.isDragging = true;
        this.startY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
        this.startScrollTop = this.element.scrollTop;
        this.thumb.style.backgroundColor = this.options.thumbHoverColor;
        document.body.style.userSelect = 'none';
    }

    onDragMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();

        const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
        const deltaY = clientY - this.startY;

        const { scrollHeight, clientHeight } = this.element;
        const trackHeight = this.track.clientHeight;
        const thumbHeight = this.thumb.clientHeight;

        const maxScroll = scrollHeight - clientHeight;
        const maxThumbTop = trackHeight - thumbHeight;
        const scrollRatio = maxScroll / maxThumbTop;

        this.element.scrollTop = this.startScrollTop + deltaY * scrollRatio;
    }

    onDragEnd() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.thumb.style.backgroundColor = this.options.thumbColor;
        document.body.style.userSelect = '';
    }

    onTrackClick(e) {
        if (e.target === this.thumb) return;

        const trackRect = this.track.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const trackHeight = this.track.clientHeight;
        const thumbHeight = this.thumb.clientHeight;

        const { scrollHeight, clientHeight } = this.element;
        const maxScroll = scrollHeight - clientHeight;

        // Center thumb on click position
        const targetThumbTop = clickY - thumbHeight / 2;
        const maxThumbTop = trackHeight - thumbHeight;
        const scrollRatio = maxScroll / maxThumbTop;

        this.element.scrollTop = Math.max(0, targetThumbTop) * scrollRatio;
    }

    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        if (this.track && this.track.parentNode) {
            this.track.parentNode.removeChild(this.track);
        }
        this.element.style.scrollbarWidth = '';
        this.element.style.msOverflowStyle = '';
    }
}
